import { useEffect, useRef } from "react"

import { useTranslation } from "react-i18next"

import type { StyleTemplateChatMessage } from "../../hooks/use-style-template-chat"
import type { PromptLibraryItem } from "@/api/models"
import type { SessionType } from "@/types"

import { MarkdownRenderer } from "@/components/markdown-renderer"
import { ScrollArea } from "@/components/ui/scroll-area"
import { ExplorerFooterToolbar } from "@/features/ai-explorer/components/explorer-footer-toolbar"
import { ChatFooter } from "@/features/chat/components/chat-footer"
import { SystemMessageLayout } from "@/features/chat/components/system-message-layout"
import { UserMessage } from "@/features/chat/components/user-message"
import styles from "@/features/chat/styles/answer.module.css"
import { cn } from "@/lib/utils"

type StyleTemplateChatProps = {
  messages: StyleTemplateChatMessage[]
  isStreaming: boolean
  streamingContent: string
  files: File[]
  onSendMessage: (message: string) => void
  onCancel: () => void
  onFilesAdded: (files: File[]) => void
  onRemoveFile: (file: File | string) => void
  sessionType: SessionType
  showWebSearchOption: boolean
  showM365Option: boolean
  showServiceDeskOption: boolean
  showNetboxOption: boolean
  hasAgentModeFeature: boolean
  isPromptsFeatureEnabled: boolean
  showHighReasoning: boolean
  enableAgent: boolean
  enableWebSearch: boolean
  enableM365: boolean
  enableServiceDesk: boolean
  enableNetbox: boolean
  enableHighReasoning: boolean
  onAgentChange: (enabled: boolean) => void
  onWebSearchChange: (enabled: boolean) => void
  onM365Change: (enabled: boolean) => void
  onServiceDeskChange: (enabled: boolean) => void
  onNetboxChange: (enabled: boolean) => void
  onHighReasoningChange: (enabled: boolean) => void
}

export function StyleTemplateChat({
  messages,
  isStreaming,
  streamingContent,
  files,
  onSendMessage,
  onCancel,
  onFilesAdded,
  onRemoveFile,
  sessionType,
  showWebSearchOption,
  showM365Option,
  showServiceDeskOption,
  showNetboxOption,
  hasAgentModeFeature,
  isPromptsFeatureEnabled,
  showHighReasoning,
  enableAgent,
  enableWebSearch,
  enableM365,
  enableServiceDesk,
  enableNetbox,
  enableHighReasoning,
  onAgentChange,
  onWebSearchChange,
  onM365Change,
  onServiceDeskChange,
  onNetboxChange,
  onHighReasoningChange,
}: StyleTemplateChatProps) {
  const { t } = useTranslation(undefined, { keyPrefix: "studioAi.styleTemplates" })
  const scrollRef = useRef<HTMLDivElement>(null)
  const setQuestionRef = useRef<((text: string) => void) | null>(null)
  const startVoiceModeRef = useRef<(() => void) | null>(null)

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth", block: "end" })
  }, [messages, streamingContent, isStreaming])

  return (
    <div className="flex h-full flex-col">
      {/* Messages */}
      <ScrollArea className="flex-1">
        <div className="flex flex-col gap-6 p-4">
          {/* Welcome message */}
          {messages.length === 0 && !isStreaming && (
            <SystemMessageLayout>
              <SystemMessageLayout.Content>
                <div className="text-muted-foreground text-sm">{t("chat.welcomeMessage")}</div>
              </SystemMessageLayout.Content>
            </SystemMessageLayout>
          )}

          {messages.map((msg) => {
            if (msg.role === "user") {
              return <UserMessage key={msg.id} message={msg.content} />
            }

            return (
              <SystemMessageLayout key={msg.id}>
                <SystemMessageLayout.Content>
                  <MarkdownRenderer
                    className="prose-sm max-w-full! wrap-anywhere *:max-w-full **:wrap-anywhere"
                    content={msg.content}
                  />
                </SystemMessageLayout.Content>
              </SystemMessageLayout>
            )
          })}

          {/* Streaming */}
          {isStreaming && (
            <SystemMessageLayout>
              <SystemMessageLayout.Content>
                {streamingContent ? (
                  <MarkdownRenderer
                    isStreaming
                    content={streamingContent}
                    className={cn(
                      "prose-sm max-w-full! wrap-anywhere *:max-w-full **:wrap-anywhere",
                      "animate-pulse"
                    )}
                  />
                ) : (
                  <div className="text-muted-foreground flex items-center gap-2 py-2 text-sm">
                    <span className={styles.loadingdots}>{t("chat.analyzing")}</span>
                  </div>
                )}
              </SystemMessageLayout.Content>
            </SystemMessageLayout>
          )}

          <div ref={scrollRef} />
        </div>
      </ScrollArea>

      {/* Footer */}
      <ChatFooter
        clearOnSend
        showChatOptions
        availableFiles={files}
        changeTokenCounts={() => {}}
        disableFileSelection={false}
        disableSend={isStreaming}
        enableHighReasoning={enableHighReasoning}
        maxTokens={128000}
        placeholder={t("chat.placeholder")}
        sessionType={sessionType}
        showCancelOption={isStreaming}
        showHighReasoning={showHighReasoning}
        totalTokenCount={0}
        onCancel={onCancel}
        onFilesAdded={onFilesAdded}
        onHighReasoningChange={onHighReasoningChange}
        onRemoveFile={onRemoveFile}
        onSend={onSendMessage}
        onGetToggleVoiceMode={(toggleFn) => {
          startVoiceModeRef.current = toggleFn
        }}
        onSetQuestion={(setter) => {
          setQuestionRef.current = setter
        }}
      >
        <ExplorerFooterToolbar
          enableAgent={enableAgent}
          enableHighReasoning={enableHighReasoning}
          enableM365={enableM365}
          enableNetbox={enableNetbox}
          enableServiceDesk={enableServiceDesk}
          enableWebSearch={enableWebSearch}
          hasAgentModeFeature={hasAgentModeFeature}
          isPromptsFeatureEnabled={isPromptsFeatureEnabled}
          isStreaming={isStreaming}
          sessionType={sessionType}
          showHighReasoning={showHighReasoning}
          showM365Option={showM365Option}
          showNetboxOption={showNetboxOption}
          showServiceDeskOption={showServiceDeskOption}
          showWebSearchOption={showWebSearchOption}
          onAgentChange={onAgentChange}
          onHighReasoningChange={onHighReasoningChange}
          onM365Change={onM365Change}
          onNetboxChange={onNetboxChange}
          onServiceDeskChange={onServiceDeskChange}
          onStartVoiceMode={() => startVoiceModeRef.current?.()}
          onWebSearchChange={onWebSearchChange}
          onPromptClicked={(prompt: PromptLibraryItem) => {
            setQuestionRef.current?.(prompt.content)
          }}
        />
      </ChatFooter>
    </div>
  )
}
